// react
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, useParams } from '@tanstack/react-router';

// api
import {
  useGetDatasetRefreshStatusQuery,
  useGetReportsInWorkspaceQuery,
  usePostDatasetRefreshRequestMutation,
} from '@/api/power-bi/powerbiApiSlice';

// components
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { showSnackbar } from '@/redux/snackbarSlice';
import ErrorBoundary from '@/routes/-ErrorBoundary';
import useWindowDimensions from '@/utils/useWindowDimensions';

import { models } from 'powerbi-client';
import { PowerBIEmbed } from 'powerbi-client-react';

export const Route = createFileRoute('/$accountId/_user/powerbi/$reportId')({
  component: RouteComponent,
  errorComponent: ErrorBoundary,
});

function RouteComponent() {
  const { t } = useTranslation('power-bi');
  // Reference to store the embedded report
  const reportRef = useRef(null);

  const { reportId } = useParams({ strict: false });
  const dispatch = useDispatch();

  const selectedAccount = useSelector(getSelectedAccount);

  const { width } = useWindowDimensions();
  const { data: monitorReports, refetch: refetchReports } =
    useGetReportsInWorkspaceQuery(
      { accountId: selectedAccount.AccountId },
      { skip: !selectedAccount.AccountId },
    );

  const embedReport = monitorReports?.find(
    (report) => String(report.Id) === String(reportId),
  );
  const datasetId = embedReport?.DatasetId;

  // Event Handlers
  const handleLoaded = () => console.log('Report loaded');
  const handleRendered = () => console.log('Report rendered');
  const handleError = () => {
    void refetchReports();
  };
  const handleVisualClicked = () => console.log('visual clicked');
  const handlePageChanged = (event) => console.log(event);

  const [isPolling, setIsPolling] = useState(false);
  const [postRefresh] = usePostDatasetRefreshRequestMutation();
  //const [pollingInterval, setPollingInterval] = useState(0);
  const pollingInterval = isPolling ? 3000 : 0;

  const { data: refreshStatus } = useGetDatasetRefreshStatusQuery(
    { datasetId },
    { skip: !isPolling || !datasetId, pollingInterval },
  );

  // Polling interval control based on `isPolling` status
  // useEffect(() => {
  //   setPollingInterval(isPolling ? 3000 : 0);
  // }, [isPolling]);

  // Initial post request on page mount to check if a refresh is needed
  useEffect(() => {
    const initiateRefresh = async () => {
      if (datasetId) {
        const result = await postRefresh({
          datasetId,
        }).unwrap();

        if (result) {
          dispatch(
            showSnackbar({
              message:
                t?.('datasetRefreshInProgress') ||
                'Dataset refresh in progress...',
              severity: 'info',
            }),
          );
          setIsPolling(true);
        } else {
          setIsPolling(false);
        }
      }
    };

    initiateRefresh();
  }, [datasetId, postRefresh, dispatch, t]);

  if (refreshStatus && isPolling) {
    setIsPolling(false);
    dispatch(
      showSnackbar({
        message:
          t?.('reloadForLatestData') ||
          'Please reload page to see latest data.',
        severity: 'info',
      }),
    );
  }

  return (
    embedReport?.EmbedToken && (
      <PowerBIEmbed
        embedConfig={{
          type: 'report',
          id: embedReport.Id,
          embedUrl: embedReport.EmbedUrl,
          accessToken: embedReport.EmbedToken,
          tokenType: models.TokenType.Embed,
          settings: {
            panes: {
              filters: {
                expanded: false,
                visible: false,
              },
            },
            background: models.BackgroundType.Transparent,
            navContentPaneEnabled: false,
            zoomLevel: width / 2000,
          },
        }}
        eventHandlers={
          new Map([
            ['loaded', handleLoaded],
            ['rendered', handleRendered],
            ['error', handleError],
            ['visualClicked', handleVisualClicked],
            ['pageChanged', handlePageChanged],
          ])
        }
        cssClassName="reportClass"
        getEmbeddedComponent={(embeddedReport) => {
          // Store the embedded report reference
          reportRef.current = embeddedReport;
        }}
      />
    )
  );
}
